import { type FormEvent, useState } from "react";

import { type CapacityReport, getCapacity, parseReviewers } from "./capacityClient";

function field(form: FormData, name: string): string {
  return String(form.get(name) ?? "").trim();
}

/** Projected review hours per priority class, and when different team sizes would finish. */
export function ReviewCapacityPage() {
  const [report, setReport] = useState<CapacityReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  function onShow(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const reviewers = parseReviewers(field(form, "reviewers"));
    if (reviewers.length === 0) {
      setError("Enter at least one reviewer count.");
      return;
    }
    getCapacity({
      engagement: field(form, "engagement"),
      run: field(form, "run"),
      reviewers,
      hoursPerDay: Number(field(form, "hours")),
    })
      .then((found) => {
        setReport(found);
        setError(null);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : String(caught));
      });
  }

  const assumed = report?.classes.some((line) => line.basis === "assumed");
  return (
    <section aria-labelledby="capacity-title">
      <h1 id="capacity-title">Review capacity</h1>
      <form onSubmit={onShow} aria-label="Plan review capacity">
        <label>
          Engagement id <input name="engagement" required />
        </label>
        <label>
          Run id <input name="run" />
        </label>
        <label>
          Reviewer counts <input name="reviewers" defaultValue="1, 2, 4" />
        </label>
        <label>
          Review hours per day{" "}
          <input name="hours" type="number" min="1" max="24" step="0.5" defaultValue="6" />
        </label>
        <button type="submit">Show capacity</button>
      </form>
      {error && <p role="alert">Could not load: {error}</p>}
      {report && (
        <>
          <p>
            {report.open_cases} open cases, about {report.projected_hours} review hours.
          </p>
          {!report.reconciled && (
            <p role="alert">
              These numbers do not match the queue, which has {report.queue_open_cases} open cases.
            </p>
          )}
          {assumed && (
            <p role="status">
              Some estimates use a planning assumption, because too few cases have been reviewed to
              measure them.
            </p>
          )}
          <table aria-label="Hours by priority">
            <thead>
              <tr>
                <th scope="col">Priority</th>
                <th scope="col">Open cases</th>
                <th scope="col">Minutes per case</th>
                <th scope="col">Basis</th>
                <th scope="col">Hours</th>
              </tr>
            </thead>
            <tbody>
              {report.classes.map((line) => (
                <tr key={line.priority_class}>
                  <th scope="row">{line.priority_class}</th>
                  <td>{line.open_cases}</td>
                  <td>{line.minutes_per_case}</td>
                  <td>{line.basis}</td>
                  <td>{line.projected_hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table aria-label="Scenarios">
            <thead>
              <tr>
                <th scope="col">Reviewers</th>
                <th scope="col">Working days</th>
                <th scope="col">Finishes</th>
              </tr>
            </thead>
            <tbody>
              {report.scenarios.map((scenario) => (
                <tr key={scenario.reviewers}>
                  <th scope="row">{scenario.reviewers}</th>
                  <td>{scenario.working_days}</td>
                  <td>{scenario.completion_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
